import React, { useEffect, useMemo, useState, useCallback } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "../css/HolidayInsights.css";

// ---------- helpers: dates ----------
const DOW = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };

function nthDowOfMonth(year, monthIndex, dow, nth) {
  // monthIndex: 0-11
  const d = new Date(year, monthIndex, 1);
  const shift = (dow - d.getDay() + 7) % 7;
  return new Date(year, monthIndex, 1 + shift + 7 * (nth - 1));
}
function lastDowOfMonth(year, monthIndex, dow) {
  const d = new Date(year, monthIndex + 1, 0); // last day of month
  const shiftBack = (d.getDay() - dow + 7) % 7;
  return new Date(year, monthIndex + 1, 0 - shiftBack);
}
function observedDateIfWeekend(date) {
  // US observed: if Sat -> Friday prior; if Sun -> Monday after
  const d = new Date(date);
  if (d.getDay() === DOW.SAT) {
    d.setDate(d.getDate() - 1);
  } else if (d.getDay() === DOW.SUN) {
    d.setDate(d.getDate() + 1);
  }
  return d;
}
function fmtLocal(date) {
  // match how you already saved Date in Firestore (toLocaleDateString())
  return new Date(date).toLocaleDateString();
}
function dayName(date) {
  return new Intl.DateTimeFormat(undefined, { weekday: "long" }).format(date);
}
function rangeDates(start, end) {
  const out = [];
  const d = new Date(start);
  while (d <= end) {
    out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}
function sum(arr, key) {
  return arr.reduce((s, x) => s + (Number(x?.[key]) || 0), 0);
}

// ---------- formatting helpers (display only) ----------
const fmtUSD = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
});
const fmtInt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

// ---------- holidays (US Federal) ----------
function getUsFederalHolidays(year) {
  const jan1 = new Date(year, 0, 1); // New Year's Day
  const mlk = nthDowOfMonth(year, 0, DOW.MON, 3); // 3rd Mon Jan
  const presidents = nthDowOfMonth(year, 1, DOW.MON, 3); // 3rd Mon Feb
  const memorial = lastDowOfMonth(year, 4, DOW.MON); // last Mon May
  const juneteenth = new Date(year, 5, 19); // June 19
  const independence = new Date(year, 6, 4); // July 4
  const labor = nthDowOfMonth(year, 8, DOW.MON, 1); // 1st Mon Sep
  const columbus = nthDowOfMonth(year, 9, DOW.MON, 2); // 2nd Mon Oct
  const veterans = new Date(year, 10, 11); // Nov 11
  const thanksgiving = nthDowOfMonth(year, 10, DOW.THU, 4); // 4th Thu Nov
  const christmas = new Date(year, 11, 25); // Dec 25

  const list = [
    { key: "newyear", name: "New Year's Day", date: jan1 },
    { key: "mlk", name: "Martin Luther King Jr. Day", date: mlk },
    { key: "presidents", name: "Presidents' Day", date: presidents },
    { key: "memorial", name: "Memorial Day", date: memorial },
    { key: "juneteenth", name: "Juneteenth", date: juneteenth },
    { key: "independence", name: "Independence Day", date: independence },
    { key: "labor", name: "Labor Day", date: labor },
    {
      key: "columbus",
      name: "Columbus / Indigenous Peoples' Day",
      date: columbus,
    },
    { key: "veterans", name: "Veterans Day", date: veterans },
    { key: "thanksgiving", name: "Thanksgiving Day", date: thanksgiving },
    { key: "christmas", name: "Christmas Day", date: christmas },
  ];

  return list.map((h) => ({ ...h, observed: observedDateIfWeekend(h.date) }));
}

function getLongWeekends(year) {
  const holidays = getUsFederalHolidays(year);
  const byKey = {};
  holidays.forEach((h) => (byKey[h.key] = h));

  const ranges = [];

  // If observed on Monday -> Sat–Mon long weekend
  holidays.forEach((h) => {
    const obs = h.observed;
    if (obs.getDay() === DOW.MON) {
      const sat = new Date(obs);
      sat.setDate(obs.getDate() - 2);
      ranges.push({ start: sat, end: obs, label: `${h.name} Weekend` });
    }
    // If observed on Friday -> Fri–Sun long weekend
    if (obs.getDay() === DOW.FRI) {
      const sun = new Date(obs);
      sun.setDate(obs.getDate() + 2);
      ranges.push({ start: obs, end: sun, label: `${h.name} Weekend` });
    }
  });

  // Thanksgiving: Thu–Sun
  if (byKey.thanksgiving) {
    const thu = byKey.thanksgiving.date;
    const sun = new Date(thu);
    sun.setDate(thu.getDate() + 3);
    ranges.push({ start: thu, end: sun, label: "Thanksgiving Weekend" });
  }

  return ranges;
}

// for older rows missing KidCount
function estimateKidCount(playCost) {
  if (!playCost || playCost <= 0) return 0;
  const prices = [30, 25, 20];
  let count = 0;
  let remaining = playCost;
  for (const p of prices) {
    if (remaining >= p) {
      remaining -= p;
      count++;
    } else break;
  }
  count += Math.floor(remaining / 20);
  return count;
}

const HolidayInsights = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]); // all year transactions

  // fetch all transactions for the selected year once
  useEffect(() => {
    (async () => {
      try {
        const ref = collection(db, "transactions");
        const q = query(ref, where("Year", "==", year));
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(data);
      } catch (e) {
        console.error("Error loading transactions for year", year, e);
      }
    })();
  }, [year]);

  const holidays = useMemo(() => getUsFederalHolidays(year), [year]);
  const longWeekends = useMemo(() => getLongWeekends(year), [year]);

  // fast lookup: date string ("MM/DD/YYYY" in your locale) => rows
  const rowsByDate = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const k = r.Date; // saved via toLocaleDateString()
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    return map;
  }, [rows]);

  const summarizeForDate = useCallback(
    (date) => {
      const key = fmtLocal(date);
      const list = rowsByDate.get(key) || [];
      const txCount = list.length;
      const kids = list.reduce(
        (s, r) =>
          s +
          (typeof r.KidCount === "number"
            ? r.KidCount
            : estimateKidCount(r.PlayCost || 0)),
        0
      );
      const total = sum(list, "TotalAmount");
      const cash = sum(list, "TotalCashAmount");
      const card = sum(list, "TotalCardAmount");
      return { txCount, kids, total, cash, card, items: list };
    },
    [rowsByDate]
  );

  function summarizeForRange(start, end) {
    const dates = rangeDates(start, end).map(fmtLocal);
    let txCount = 0,
      kids = 0,
      total = 0,
      cash = 0,
      card = 0;
    dates.forEach((dk) => {
      const list = rowsByDate.get(dk) || [];
      txCount += list.length;
      kids += list.reduce(
        (s, r) =>
          s +
          (typeof r.KidCount === "number"
            ? r.KidCount
            : estimateKidCount(r.PlayCost || 0)),
        0
      );
      total += sum(list, "TotalAmount");
      cash += sum(list, "TotalCashAmount");
      card += sum(list, "TotalCardAmount");
    });
    return { txCount, kids, total, cash, card };
  }

  const totalHolidaysSummary = useMemo(() => {
    return holidays.reduce(
      (acc, h) => {
        const s = summarizeForDate(h.observed);
        acc.txCount += s.txCount;
        acc.kids += s.kids;
        acc.total += s.total;
        acc.cash += s.cash;
        acc.card += s.card;
        return acc;
      },
      { txCount: 0, kids: 0, total: 0, cash: 0, card: 0 }
    );
  }, [holidays, summarizeForDate]);

  return (
    <div className="holiday-insights">
      <div className="hi-header">
        <h2>Holiday Insights</h2>
        <div className="hi-controls">
          <label className="hi-year">
            Year
            <input
              type="number"
              min="2000"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            />
          </label>
        </div>
      </div>

      <div className="hi-summary">
        <div className="chip">
          <span>Holidays Tx</span>
          <strong>{fmtInt.format(totalHolidaysSummary.txCount)}</strong>
        </div>
        <div className="chip">
          <span>Kids</span>
          <strong>{fmtInt.format(totalHolidaysSummary.kids)}</strong>
        </div>
        <div className="chip">
          <span>Cash</span>
          <strong>{fmtUSD.format(totalHolidaysSummary.cash)}</strong>
        </div>
        <div className="chip">
          <span>Card</span>
          <strong>{fmtUSD.format(totalHolidaysSummary.card)}</strong>
        </div>
        <div className="chip emphasis">
          <span>Total</span>
          <strong>{fmtUSD.format(totalHolidaysSummary.total)}</strong>
        </div>
      </div>

      <div className="hi-card">
        <div className="hi-card-head">
          <h3>US Federal Holidays</h3>
          <p className="hi-note">
            Counts use the <b>observed</b> day (Mon/Fri if the actual date is on
            a weekend).
          </p>
        </div>

        <div className="hi-table-wrap">
          <table className="hi-table">
            <thead>
              <tr>
                <th className="sticky left">#</th>
                <th className="sticky">Holiday</th>
                <th className="sticky">Date</th>
                <th className="sticky">Day</th>
                <th className="sticky right">Transactions</th>
                <th className="sticky right">Kids</th>
                <th className="sticky right">Cash</th>
                <th className="sticky right">Card</th>
                <th className="sticky right">Total</th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((h, i) => {
                const s = summarizeForDate(h.observed);
                return (
                  <tr key={h.key}>
                    <td className="left">{i + 1}</td>
                    <td>{h.name}</td>
                    <td>{fmtLocal(h.observed)}</td>
                    <td>{dayName(h.observed)}</td>
                    <td className="right">{fmtInt.format(s.txCount)}</td>
                    <td className="right">{fmtInt.format(s.kids)}</td>
                    <td className="right">{fmtUSD.format(s.cash)}</td>
                    <td className="right">{fmtUSD.format(s.card)}</td>
                    <td className="right strong">{fmtUSD.format(s.total)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="left" colSpan="4">
                  <b>Totals</b>
                </td>
                <td className="right">
                  <b>{fmtInt.format(totalHolidaysSummary.txCount)}</b>
                </td>
                <td className="right">
                  <b>{fmtInt.format(totalHolidaysSummary.kids)}</b>
                </td>
                <td className="right">
                  <b>{fmtUSD.format(totalHolidaysSummary.cash)}</b>
                </td>
                <td className="right">
                  <b>{fmtUSD.format(totalHolidaysSummary.card)}</b>
                </td>
                <td className="right">
                  <b>{fmtUSD.format(totalHolidaysSummary.total)}</b>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="hi-card">
        <div className="hi-card-head">
          <h3>Long Weekends</h3>
          <p className="hi-note">
            Defined as Fri–Sun when holiday is observed Friday, Sat–Mon when
            observed Monday, and Thu–Sun for Thanksgiving.
          </p>
        </div>

        <div className="hi-table-wrap">
          <table className="hi-table">
            <thead>
              <tr>
                <th className="sticky left">#</th>
                <th className="sticky">Weekend</th>
                <th className="sticky">Range</th>
                <th className="sticky right">Transactions</th>
                <th className="sticky right">Kids</th>
                <th className="sticky right">Cash</th>
                <th className="sticky right">Card</th>
                <th className="sticky right">Total</th>
              </tr>
            </thead>
            <tbody>
              {longWeekends.map((w, i) => {
                const s = summarizeForRange(w.start, w.end);
                const rangeLabel = `${fmtLocal(w.start)} → ${fmtLocal(w.end)}`;
                return (
                  <tr key={`${w.label}-${i}`}>
                    <td className="left">{i + 1}</td>
                    <td>{w.label}</td>
                    <td>{rangeLabel}</td>
                    <td className="right">{fmtInt.format(s.txCount)}</td>
                    <td className="right">{fmtInt.format(s.kids)}</td>
                    <td className="right">{fmtUSD.format(s.cash)}</td>
                    <td className="right">{fmtUSD.format(s.card)}</td>
                    <td className="right strong">{fmtUSD.format(s.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Back to top button */}
      <button
        className="hi-back-to-top"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
        title="Back to top"
      >
        ↑
      </button>
    </div>
  );
};

export default HolidayInsights;
